import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { api, formatDate } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  User,
  CaretRight,
  Archive,
  ArrowClockwise,
  Eye,
  DownloadSimple,
  MagnifyingGlass,
  PencilSimple,
  UploadSimple,
  X
} from '@phosphor-icons/react';

const statusConfig = {
  draft: { label: 'Bozza', color: 'bg-gray-100 text-gray-800', icon: FileText },
  pending: { label: 'In revisione', color: 'bg-amber-100 text-amber-800', icon: Clock },
  changes_requested: { label: 'Richieste modifiche', color: 'bg-orange-100 text-orange-800', icon: PencilSimple },
  approved: { label: 'Approvato', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  rejected: { label: 'Rifiutato', color: 'bg-red-100 text-red-800', icon: XCircle }
};

const localeMap = { it: 'it-CH', de: 'de-CH', fr: 'fr-CH', en: 'en-GB' };

export const WorkflowPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [toApprove, setToApprove] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [allWorkflows, setAllWorkflows] = useState([]);
  const [approvers, setApprovers] = useState([]);
  
  // Dialog states
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [showResubmitDialog, setShowResubmitDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [actionType, setActionType] = useState('');
  const [actionNotes, setActionNotes] = useState('');
  const [resubmitApprover, setResubmitApprover] = useState('');
  const [resubmitFile, setResubmitFile] = useState(null);
  const [uploadingNewVersion, setUploadingNewVersion] = useState(false);
  const [processing, setProcessing] = useState(false);

  const isApprover = user?.role === 'approver' || user?.role === 'admin';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [toApproveRes, myRequestsRes, approversRes] = await Promise.all([
        isApprover ? api.getWorkflowsToApprove() : Promise.resolve({ data: [] }),
        api.getMyWorkflowRequests(),
        api.getApprovers()
      ]);
      
      setToApprove(toApproveRes.data || []);
      setMyRequests(myRequestsRes.data || []);
      setApprovers(approversRes.data || []);
      
      // If admin, also fetch all workflows
      if (user?.role === 'admin') {
        const allRes = await api.getAllWorkflows();
        setAllWorkflows(allRes.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch workflows:', error);
      toast.error('Errore nel caricamento dei workflow');
    } finally {
      setLoading(false);
    }
  }, [isApprover, user?.role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAction = async () => {
    if (!selectedWorkflow || !actionType) return;
    
    // require notes for reject and request_changes
    if ((actionType === 'reject' || actionType === 'request_changes') && !actionNotes.trim()) {
      toast.error(actionType === 'reject' 
        ? t('workflow.errorRejectReason') 
        : t('workflow.errorChangesReason'));
      return;
    }
    
    setProcessing(true);
    try {
      await api.workflowAction(selectedWorkflow.id, actionType, actionNotes);
      if (actionType === 'approve') {
        toast.success(t('workflow.approveSuccess'));
      } else if (actionType === 'reject') {
        toast.success(t('workflow.rejectSuccess'));
      } else if (actionType === 'request_changes') {
        toast.success(t('workflow.changesRequestedSuccess'));
      }
      setShowActionDialog(false);
      setActionNotes('');
      fetchData();
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setProcessing(false);
    }
  };

  const handleResubmit = async () => {
    if (!selectedWorkflow || !resubmitApprover) {
      toast.error('Seleziona un approvatore');
      return;
    }
    
    setProcessing(true);
    try {
      let documentId = selectedWorkflow.document_id;
      
      // If user selected a new file, upload it first as a new version
      if (resubmitFile) {
        setUploadingNewVersion(true);
        try {
          const uploadRes = await api.uploadDocument(
            resubmitFile, 
            null, // folder_id will be inherited
            null, // tags will be inherited
            documentId // update_existing - creates new version
          );
          documentId = uploadRes.data.id; // Use the new document ID
          toast.success(`Documento aggiornato alla versione ${uploadRes.data.version}`);
        } catch (uploadErr) {
          toast.error('Errore nel caricamento del file: ' + (uploadErr.response?.data?.detail || uploadErr.message));
          setUploadingNewVersion(false);
          setProcessing(false);
          return;
        }
        setUploadingNewVersion(false);
      }
      
      // Now resubmit the workflow with the (possibly new) document ID
      await api.resubmitWorkflow(selectedWorkflow.id, documentId, resubmitApprover, actionNotes);
      toast.success('Documento reinviato per approvazione');
      setShowResubmitDialog(false);
      setActionNotes('');
      setResubmitApprover('');
      setResubmitFile(null);
      fetchData();
    } catch (error) {
      toast.error('Errore nel reinvio');
    } finally {
      setProcessing(false);
    }
  };

  const openActionDialog = (workflow, type) => {
    setSelectedWorkflow(workflow);
    setActionType(type);
    setActionNotes('');
    setShowActionDialog(true);
  };

  const openHistoryDialog = (workflow) => {
    setSelectedWorkflow(workflow);
    setShowHistoryDialog(true);
  };

  const openResubmitDialog = (workflow) => {
    setSelectedWorkflow(workflow);
    setActionNotes('');
    setResubmitApprover('');
    setShowResubmitDialog(true);
  };

  // Preview document (use PDF/A version)
  const handlePreview = async (workflow) => {
    setSelectedWorkflow(workflow);
    setPreviewLoading(true);
    setShowPreviewDialog(true);
    
    try {
      // Use PDF/A preview for workflow documents
      const url = api.getDocumentPreviewPdfaUrl(workflow.document_id);
      setPreviewUrl(url);
    } catch (error) {
      toast.error('Errore nel caricamento anteprima');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Download document
  const handleDownload = async (workflow) => {
    try {
      await api.downloadDocument(workflow.document_id, workflow.document_name);
      toast.success('Download avviato');
    } catch (error) {
      toast.error('Errore nel download');
    }
  };

  const StatusBadge = ({ status }) => {
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${config.color}`}>
        <Icon size={14} />
        {config.label}
      </span>
    );
  };

  const WorkflowCard = ({ workflow, showActions = false, showResubmit = false }) => (
    <div className="bg-white border border-[#E4E4E7] p-4 hover:border-[#002FA7]/30 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <FileText size={20} className="text-[#002FA7]" />
            <h3 className="font-medium text-[#09090B]">{workflow.document_name}</h3>
            <StatusBadge status={workflow.status} />
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm text-[#52525B]">
            <div>
              <span className="text-xs uppercase tracking-wider">Richiesto da</span>
              <p className="font-medium text-[#09090B]">{workflow.requester_email}</p>
            </div>
            <div>
              <span className="text-xs uppercase tracking-wider">Approvatore</span>
              <p className="font-medium text-[#09090B]">{workflow.approver_email}</p>
            </div>
            <div>
              <span className="text-xs uppercase tracking-wider">Data richiesta</span>
              <p>{formatDate(workflow.created_at, localeMap[language])}</p>
            </div>
            {workflow.notes && (
              <div>
                <span className="text-xs uppercase tracking-wider">Note</span>
                <p className="truncate">{workflow.notes}</p>
              </div>
            )}
          </div>
          
          {/* Document actions row */}
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#E4E4E7]">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePreview(workflow)}
              className="text-[#002FA7] border-[#002FA7]/30 hover:bg-[#002FA7]/5"
              data-testid={`preview-doc-${workflow.id}`}
            >
              <MagnifyingGlass size={16} className="mr-1" />
              Anteprima
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDownload(workflow)}
              className="text-[#52525B]"
              data-testid={`download-doc-${workflow.id}`}
            >
              <DownloadSimple size={16} className="mr-1" />
              Scarica
            </Button>
          </div>
        </div>
        <div className="flex flex-col gap-2 ml-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openHistoryDialog(workflow)}
            className="text-[#52525B]"
          >
            <Eye size={16} className="mr-1" />
            Storico
          </Button>
          {showActions && workflow.status === 'pending' && (
            <>
              <Button
                size="sm"
                onClick={() => openActionDialog(workflow, 'approve')}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <CheckCircle size={16} className="mr-1" />
                {t('workflow.approve')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => openActionDialog(workflow, 'request_changes')}
                className="border-orange-500 text-orange-600 hover:bg-orange-50"
              >
                <PencilSimple size={16} className="mr-1" />
                {t('workflow.requestChanges')}
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => openActionDialog(workflow, 'reject')}
              >
                <XCircle size={16} className="mr-1" />
                {t('workflow.reject')}
              </Button>
            </>
          )}
          {showResubmit && (workflow.status === 'rejected' || workflow.status === 'changes_requested') && (
            <Button
              size="sm"
              onClick={() => openResubmitDialog(workflow)}
              className="bg-[#002FA7] hover:bg-[#002075] text-white"
            >
              <ArrowClockwise size={16} className="mr-1" />
              {t('workflow.resubmit')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  const EmptyState = ({ message }) => (
    <div className="text-center py-12 text-[#52525B]">
      <FileText size={48} className="mx-auto mb-4 opacity-30" />
      <p>{message}</p>
    </div>
  );

  return (
    <Layout>
      <div className="page-header">
        <h1 className="page-title" data-testid="workflow-title">
          Workflow Approvazione
        </h1>
      </div>

      <div className="content-area">
        <Tabs defaultValue={isApprover ? "to-approve" : "my-requests"} className="w-full">
          <TabsList className="mb-6">
            {isApprover && (
              <TabsTrigger value="to-approve" className="relative">
                Da Approvare
                {toApprove.length > 0 && (
                  <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {toApprove.length}
                  </span>
                )}
              </TabsTrigger>
            )}
            <TabsTrigger value="my-requests">Le Mie Richieste</TabsTrigger>
            {user?.role === 'admin' && (
              <TabsTrigger value="all">Tutti i Workflow</TabsTrigger>
            )}
          </TabsList>

          {isApprover && (
            <TabsContent value="to-approve">
              {loading ? (
                <div className="text-center py-8">Caricamento...</div>
              ) : toApprove.length === 0 ? (
                <EmptyState message="Nessun documento in attesa di approvazione" />
              ) : (
                <div className="space-y-4">
                  {toApprove.map(workflow => (
                    <WorkflowCard 
                      key={workflow.id} 
                      workflow={workflow} 
                      showActions={true}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          <TabsContent value="my-requests">
            {loading ? (
              <div className="text-center py-8">Caricamento...</div>
            ) : myRequests.length === 0 ? (
              <EmptyState message="Non hai ancora inviato documenti per approvazione" />
            ) : (
              <div className="space-y-4">
                {myRequests.map(workflow => (
                  <WorkflowCard 
                    key={workflow.id} 
                    workflow={workflow}
                    showResubmit={true}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {user?.role === 'admin' && (
            <TabsContent value="all">
              {loading ? (
                <div className="text-center py-8">Caricamento...</div>
              ) : allWorkflows.length === 0 ? (
                <EmptyState message="Nessun workflow presente" />
              ) : (
                <div className="space-y-4">
                  {allWorkflows.map(workflow => (
                    <WorkflowCard 
                      key={workflow.id} 
                      workflow={workflow}
                      showActions={workflow.status === 'pending' && workflow.approver_id === user?.id}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Action Dialog (Approve/Reject/Request Changes) */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' && t('workflow.approveDocument')}
              {actionType === 'reject' && t('workflow.rejectDocument')}
              {actionType === 'request_changes' && t('workflow.requestChangesDocument')}
            </DialogTitle>
            <DialogDescription>
              {selectedWorkflow?.document_name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
              {actionType === 'approve' ? t('workflow.notesOptional') : t('workflow.notesRequired')}
            </Label>
            <Textarea
              value={actionNotes}
              onChange={(e) => setActionNotes(e.target.value)}
              placeholder={
                actionType === 'approve' 
                  ? t('workflow.notesPlaceholderOptional')
                  : actionType === 'request_changes'
                    ? t('workflow.notesPlaceholderChanges')
                    : t('workflow.notesPlaceholderReject')
              }
              className="mt-2"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleAction}
              disabled={processing || ((actionType === 'reject' || actionType === 'request_changes') && !actionNotes.trim())}
              className={
                actionType === 'approve' 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : actionType === 'request_changes'
                    ? 'bg-orange-500 hover:bg-orange-600 text-white'
                    : 'bg-red-600 hover:bg-red-700 text-white'
              }
            >
              {processing ? t('common.processing') : (
                actionType === 'approve' ? t('workflow.approve') : 
                actionType === 'request_changes' ? t('workflow.requestChanges') :
                t('workflow.reject')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Storico Workflow</DialogTitle>
            <DialogDescription>
              {selectedWorkflow?.document_name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 max-h-[400px] overflow-y-auto">
            {selectedWorkflow?.history?.map((entry, index) => (
              <div key={index} className="flex gap-3 mb-4 last:mb-0">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#002FA7]/10 flex items-center justify-center">
                  {(entry.action === 'submitted' || entry.action === 'resubmitted') ? (
                    <CaretRight size={16} className="text-[#002FA7]" />
                  ) : entry.action === 'approve' ? (
                    <CheckCircle size={16} className="text-green-600" />
                  ) : entry.action === 'request_changes' ? (
                    <PencilSimple size={16} className="text-orange-500" />
                  ) : (
                    <XCircle size={16} className="text-red-600" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-[#09090B]">
                      {entry.action === 'submitted' && t('workflow.historySubmitted')}
                      {entry.action === 'resubmitted' && t('workflow.historyResubmitted')}
                      {entry.action === 'approve' && t('workflow.historyApproved')}
                      {entry.action === 'reject' && t('workflow.historyRejected')}
                      {entry.action === 'request_changes' && t('workflow.historyChangesRequested')}
                    </span>
                  </div>
                  <p className="text-xs text-[#52525B]">
                    {entry.user_email} • {formatDate(entry.timestamp, localeMap[language])}
                  </p>
                  {entry.notes && (
                    <p className="mt-1 text-sm text-[#52525B] bg-[#F4F4F5] p-2 rounded">
                      {entry.notes}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistoryDialog(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resubmit Dialog */}
      <Dialog open={showResubmitDialog} onOpenChange={(open) => {
        if (!open) {
          setResubmitFile(null);
        }
        setShowResubmitDialog(open);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reinvia per Approvazione</DialogTitle>
            <DialogDescription>
              {selectedWorkflow?.document_name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Upload new version section */}
            <div className="p-4 border-2 border-dashed border-[#E4E4E7] rounded-sm bg-[#FAFAFA]">
              <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-2 block">
                Carica versione modificata (opzionale)
              </Label>
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  id="resubmit-file"
                  className="hidden"
                  onChange={(e) => setResubmitFile(e.target.files?.[0] || null)}
                />
                <label
                  htmlFor="resubmit-file"
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-[#E4E4E7] rounded-sm cursor-pointer hover:bg-[#F4F4F5] transition-colors"
                >
                  <UploadSimple size={16} className="text-[#002FA7]" />
                  <span className="text-sm">Seleziona file</span>
                </label>
                {resubmitFile && (
                  <div className="flex items-center gap-2 flex-1 bg-white px-3 py-2 rounded-sm border border-[#E4E4E7]">
                    <FileText size={16} className="text-[#002FA7]" />
                    <span className="text-sm truncate flex-1">{resubmitFile.name}</span>
                    <button
                      onClick={() => setResubmitFile(null)}
                      className="p-1 hover:bg-[#F4F4F5] rounded"
                    >
                      <X size={14} className="text-[#52525B]" />
                    </button>
                  </div>
                )}
              </div>
              <p className="text-xs text-[#A1A1AA] mt-2">
                Se carichi un nuovo file, verrà creata una nuova versione del documento
              </p>
            </div>
            
            <div>
              <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                Approvatore *
              </Label>
              <Select value={resubmitApprover} onValueChange={setResubmitApprover}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Seleziona approvatore" />
                </SelectTrigger>
                <SelectContent>
                  {approvers.map(approver => (
                    <SelectItem key={approver.id} value={approver.id}>
                      {approver.name} ({approver.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                Note aggiuntive
              </Label>
              <Textarea
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                placeholder="Descrivi le modifiche apportate..."
                className="mt-2"
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResubmitDialog(false)}>
              Annulla
            </Button>
            <Button
              onClick={handleResubmit}
              disabled={processing || !resubmitApprover}
              className="bg-[#002FA7] hover:bg-[#002075] text-white"
            >
              {uploadingNewVersion ? 'Caricamento file...' : processing ? 'Elaborazione...' : resubmitFile ? 'Carica e Reinvia' : 'Reinvia'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Anteprima Documento</DialogTitle>
            <DialogDescription>
              {selectedWorkflow?.document_name}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-[500px] bg-[#F4F4F5] rounded overflow-hidden">
            {previewLoading ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-[#52525B]">Caricamento anteprima...</p>
              </div>
            ) : previewUrl ? (
              <iframe
                src={previewUrl}
                className="w-full h-[500px] border-0"
                title="Document Preview"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-[#52525B]">Anteprima non disponibile</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Chiudi
            </Button>
            <Button
              onClick={() => selectedWorkflow && handleDownload(selectedWorkflow)}
              className="bg-[#002FA7] hover:bg-[#002075] text-white"
            >
              <DownloadSimple size={16} className="mr-2" />
              Scarica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
